/**
 * Build a matcher that decides whether a given query key targets a per-id key
 * produced by `keyBuilder`, and if so returns the id.
 *
 * Contract: per-id key builders produce `[...staticPrefix, id]` — the id is
 * always the last element. Providers use this to observe external
 * writes/refetches on per-id keys (e.g. `commonQueryKeys.item(N)`) and reflect
 * them into the accumulated map without hard-coding key shapes.
 *
 * The static prefix is captured by probing the builder once at module load.
 * If the builder ever changes shape, this matcher follows automatically.
 */
export const buildIdKeyMatcher = <K extends number | string>(
  keyBuilder: (id: K) => readonly unknown[],
): ((queryKey: readonly unknown[]) => K | null) => {
  const probe = keyBuilder(0 as K);
  const expectedLength = probe.length;
  const idIndex = expectedLength - 1;
  const prefix = probe.slice(0, idIndex);

  return (queryKey) => {
    if (queryKey.length !== expectedLength) return null;
    for (let i = 0; i < idIndex; i++) {
      if (queryKey[i] !== prefix[i]) return null;
    }
    const candidate = queryKey[idIndex];
    if (typeof candidate === 'number' || typeof candidate === 'string') {
      return candidate as K;
    }
    return null;
  };
};
